import { useMemo, useState } from "react";
import { Check, ChevronRight, Loader2, Search } from "lucide-react";
import { CATEGORIES } from "../utils/Categories";

export default function ShippingAssignmentsPanel({ businessId, products, shipping, onSaveAll }) {
  const [activeProfile, setActiveProfile] = useState(null);
  const [q, setQ] = useState("");

  const {
    profiles, assignments, flags, busy, error,
    validateBusinessRules,
    toggleProductForProfile, setAllForProfile, setAllForCategory,
  } = shipping;

  // Only include products that are marked for Delivery
const productList = useMemo(() => {
  const norm = (s) => (s||"").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g,"").toLowerCase();
  const nQ = norm(q);
  return (products || [])
    .filter(p => (flags[String(p.id)]?.delivery)) // only show Delivery products
    .filter(p => !nQ || norm(p.name).includes(nQ))
    .map(p => ({
      ...p,
      mainCategory: getMainCategoryName(p?.category || []),
    }));
}, [products, q, flags]);


  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of productList) {
      const key = p.mainCategory || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()); // [ [category, products[]], ... ]
  }, [productList]);

  const selectedIdsForActive = new Set(assignments[activeProfile] || []);
  const allIds = productList.map(p => String(p.id));

  const allSelected = activeProfile && selectedIdsForActive.size === allIds.length && allIds.length > 0;
  const someSelected = activeProfile && selectedIdsForActive.size > 0 && selectedIdsForActive.size < allIds.length;

  const live = useMemo(
    () => validateBusinessRules ? validateBusinessRules(products) : { ok: true, details: {} },
    [validateBusinessRules, products, flags, assignments]
  );
  const missingDeliveryCount = live?.details?.missingDeliveryProfile?.length || 0;

  return (
    <div className="grid md:grid-cols-[260px,1fr] gap-4">
        {missingDeliveryCount > 0 && (
            <div className="mx-3 my-2 rounded-md bg-amber-50 text-amber-800 text-xs p-2">
            {missingDeliveryCount} delivery product{missingDeliveryCount > 1 ? "s are" : " is"} missing a
            shipping profile. Assign at least one profile.
            </div>
        )}
      <aside className="rounded-xl border bg-white overflow-hidden">
        <div className="p-3 border-b flex items-center gap-2">
          <Search size={16} className="text-gray-400" />
          <input
            className="w-full outline-none text-sm"
            placeholder="Filter products…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            inputMode="search"
          />
        </div>
        <ul className="max-h-[60vh] overflow-y-auto">
          {profiles.map((p) => {
            const name = p.name || p;
            const count = (assignments[name]?.length || 0);
            const active = name === activeProfile;
            return (
              <li key={name}>
                <button
                  type="button"
                  onClick={() => setActiveProfile(name)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50 ${active ? "bg-indigo-50" : ""}`}
                >
                  <span className="truncate">{name}</span>
                  <span className="text-xs text-gray-500">{count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
      {/* Right: assignment (Delivery-only products) */}
      <section className="rounded-xl border bg-white">
        {!activeProfile ? (
          <div className="p-6 text-sm text-gray-600">Pick a shipping profile to assign products.</div>
        ) : (
          <>
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-white p-3">
              <div className="flex items-center gap-2 text-sm">
                <TriStateCheckbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onChange={(on) => setAllForProfile(activeProfile, on)}
                />
                <span className="font-medium">Apply “{activeProfile}” to all products</span>
              </div>
            </div>
            <div className="divide-y">
              {grouped.map(([cat, items]) => {
                const catIds = items.map(i => String(i.id));
                const selectedInCat = catIds.filter(id => selectedIdsForActive.has(id)).length;
                const catAll = selectedInCat === catIds.length && catIds.length > 0;
                const catSome = selectedInCat > 0 && selectedInCat < catIds.length;

                return (
                  <div key={cat}>
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                      <div className="flex items-center gap-2 text-sm">
                        <TriStateCheckbox
                          checked={catAll ? true : catSome ? "indeterminate" : false}
                          onChange={(on) => setAllForCategory(activeProfile, catIds, on)}
                        />
                        <span className="font-medium">{cat}</span>
                        <span className="text-xs text-gray-500">({items.length})</span>
                      </div>
                      <div className="text-xs text-gray-500">Select all in category</div>
                    </div>

                    <ul>
                      {items.map((p) => {
                        const pid = String(p.id);
                        const assigned = selectedIdsForActive.has(pid);
                        return (
                          <li key={pid} className="border-t">
                            <div className="flex items-center gap-3 px-3 py-2">
                              <label className="flex items-center gap-2 min-w-[240px]">
                                <input
                                  type="checkbox"
                                  checked={assigned}
                                  onChange={(e) => toggleProductForProfile(activeProfile, pid, e.target.checked)}
                                  aria-label={`Assign ${activeProfile} to ${p.name}`}
                                />
                                <span className="truncate">{p.name}</span>
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/* ---------- tiny bits ---------- */
function getMainCategoryName(pathIds = []) {
  if (!Array.isArray(pathIds) || !pathIds.length) return null;
  const topId = String(pathIds[0]);
  const root = (CATEGORIES || []).find((n) => String(n.id) === topId);
  return root?.name || null;
}

function TriStateCheckbox({ checked, onChange }) {
  // checked: true | false | "indeterminate"
  return (
    <input
      type="checkbox"
      ref={(el) => {
        if (el) el.indeterminate = checked === "indeterminate";
      }}
      checked={checked === true}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}
